import { useEffect, useRef } from 'react';
import './ConfirmDialog.css';

export interface ConfirmDialogProps {
  /** Whether the dialog is shown. */
  open: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Style the confirm button as a destructive action. */
  danger?: boolean;
  /** Disable the confirm button + show progress while the action runs. */
  busy?: boolean;
  /** Inline error surfaced under the message (e.g. a failed delete). */
  error?: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * A small focus-trapped confirm modal. Escape and backdrop-click cancel;
 * focus moves to the confirm button on open and is restored on close. Uses the
 * `--om-*` design tokens; the danger variant tints the confirm button with
 * `--om-error`.
 */
export default function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  danger = false,
  busy = false,
  error = null,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const confirmRef = useRef<HTMLButtonElement>(null);
  const previouslyFocused = useRef<Element | null>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    previouslyFocused.current = document.activeElement;
    confirmRef.current?.focus();
    return () => {
      // Restore focus to whatever opened the dialog (the menu/row).
      if (previouslyFocused.current instanceof HTMLElement) {
        previouslyFocused.current.focus();
      }
    };
  }, [open]);

  if (!open) {
    return null;
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === 'Escape' && !busy) {
      e.stopPropagation();
      onCancel();
    }
  };

  return (
    <div
      className="om-modal-backdrop"
      onMouseDown={() => {
        if (!busy) {
          onCancel();
        }
      }}
      onKeyDown={onKeyDown}
    >
      <div
        className="om-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="om-modal-title"
        // Keep clicks inside the card from reaching the backdrop dismiss.
        onMouseDown={(e) => e.stopPropagation()}
      >
        <h2 id="om-modal-title" className="om-modal__title">
          {title}
        </h2>
        <p className="om-modal__message">{message}</p>
        {error !== null && error !== '' ? (
          <p className="om-modal__error" role="alert">
            {error}
          </p>
        ) : null}
        <div className="om-modal__actions">
          <button
            type="button"
            className="om-modal__btn"
            onClick={onCancel}
            disabled={busy}
          >
            {cancelLabel}
          </button>
          <button
            ref={confirmRef}
            type="button"
            className={
              danger ? 'om-modal__btn om-modal__btn--danger' : 'om-modal__btn om-modal__btn--primary'
            }
            onClick={onConfirm}
            disabled={busy}
          >
            {busy ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
